import React from 'react';
import { useTranslation } from 'react-i18next';
import SeoMeta from '../components/SeoMeta';
import { useLocalizedUrl } from '../utils/urlHelper';
import { PageHero, SectionIntro, ClosingCta, usePrimaryCta } from '../components/common/B2B';
import { Section, HeroChecks, TwoColumns, RelatedSection, RowsTable } from '../components/common/B2BSections';
import { otherTrustPages, RELATED_TITLE } from '../components/common/trustPages';

// Confianza · Cumplimiento normativo (nov-2026), rediseño B2B: una tabla con cada
// norma, qué regula y cómo la aplica BlueCMR (enlaces a BOE/EUR-Lex solo donde
// están comprobados: los del DeCA salen de su página), los requisitos comunes y
// cómo se cumplen, y el alcance. eFTI con la fecha ya publicada en la web (julio
// de 2027) y sin decir que BlueCMR sea plataforma certificada. ENS con la fórmula
// textual de OCA, sin categoría (criterio de Jesús, 25-ago-2026).
// Hero con foto (4-nov): vista aérea de un parque logístico (Jesús descartó la de la
// inspección en carretera por «perspectiva extraña»). GPT Image 2,
// marketing/prompts-imagenes/hero-cumplimiento-aerea.txt, gpt-01.
const HERO_IMAGE = { src: '/images/confianza/hero-cumplimiento.webp', position: '70% 50%' };

const PAGE_URL = { es: 'https://www.bluecmr.com/confianza/cumplimiento-normativo', en: 'https://www.bluecmr.com/en/trust/regulatory-compliance' };
const EURLEX = (lang, celex) => `https://eur-lex.europa.eu/legal-content/${lang === 'es' ? 'ES' : 'EN'}/TXT/?uri=CELEX:${celex}`;

const copy = (lang, url) => ({
  es: {
    seoTitle: 'Cumplimiento normativo de los documentos de transporte | BlueCMR',
    seoDescription: 'Las normas que cumplen los documentos de BlueCMR: eIDAS, Protocolo eCMR, eFTI, ADR, Ley 15/2009, Orden FOM/2861/2012 (DeCA), ENS y RGPD.',
    seoKeywords: 'cumplimiento normativo transporte, eidas, protocolo ecmr, deca normativa, orden fom/2861/2012, resolución 5 junio 2026, efti, ens',
    heroEyebrow: 'Confianza · Cumplimiento normativo',
    heroTitle: 'Las normas que cumple cada documento',
    heroText: 'BlueCMR está diseñada para los requisitos técnicos y jurídicos de la normativa europea y española sobre documentos de transporte por carretera, firma electrónica y seguridad de la información.',
    heroChecks: ['eIDAS', 'Protocolo eCMR', 'DeCA', 'ENS'],
    head: ['Norma', 'Qué regula', 'Cómo la aplica BlueCMR'],
    groups: [
      {
        title: 'Europa e internacional',
        rows: [
          { title: 'Reglamento eIDAS', sub: '(UE) 910/2014', href: EURLEX('es', '32014R0910'), text: 'La identificación electrónica y los servicios de confianza: firma, sello y sello de tiempo electrónicos.', aside: 'Firma electrónica avanzada (art. 26) y sellos de tiempo en cada documento.' },
          { title: 'Protocolo eCMR', sub: 'Convenio CMR, Ginebra, 2008', text: 'La carta de porte electrónica en el transporte internacional por carretera.', aside: 'eCMR con la misma validez que el papel en los países que lo han ratificado.' },
          { title: 'Reglamento eFTI', sub: '(UE) 2020/1056', href: EURLEX('es', '32020R1056'), text: 'El intercambio electrónico de información de transporte con las autoridades, que deberán aceptarla desde julio de 2027 por plataformas certificadas.', aside: 'Datos de cada documento según el conjunto común de datos de eFTI.' },
          { title: 'ADR', sub: 'Capítulo 5.4', text: 'La documentación del transporte de mercancías peligrosas por carretera.', aside: 'Documento de transporte con las menciones del capítulo 5.4, junto al DeCA.' },
        ],
      },
      {
        title: 'España',
        rows: [
          { title: 'Ley 15/2009', sub: 'Contrato de transporte terrestre', text: 'El contrato de transporte y la carta de porte.', aside: 'Carta de porte digital con los datos que exige.' },
          { title: 'Orden FOM/2861/2012', href: 'https://www.boe.es/buscar/doc.php?id=BOE-A-2013-154', text: 'El documento de control en el transporte por carretera.', aside: 'DeCA con todos sus datos, también dentro de la carta de porte o el albarán.' },
          { title: 'Ley 9/2025', sub: 'Movilidad Sostenible', href: 'https://www.boe.es/buscar/act.php?id=BOE-A-2025-24545', text: 'La obligatoriedad del formato electrónico del documento de control.', aside: 'Documento de control solo en formato electrónico.' },
          { title: 'Resolución de 5 de junio de 2026', href: 'https://www.boe.es/buscar/doc.php?id=BOE-A-2026-12784', text: 'Los requisitos técnicos del DeCA.', aside: 'PDF con código QR y los requisitos técnicos de la Resolución.' },
        ],
      },
      {
        title: 'Seguridad y datos',
        rows: [
          { title: 'Esquema Nacional de Seguridad', sub: 'Real Decreto 311/2022', to: url('/confianza/seguridad'), text: 'La seguridad de los sistemas de información.', aside: 'BLUECMR, S.L.U. dispone de un sistema de gestión certificado de acuerdo al RD 311/2022 (ENS) por OCA CERT.' },
          { title: 'RGPD', sub: '(UE) 2016/679', href: EURLEX('es', '32016R0679'), text: 'La protección de los datos personales.', aside: 'Datos alojados en la Unión Europea y contrato de encargo del tratamiento (DPA).' },
        ],
      },
    ],
    reqEyebrow: 'Requisitos comunes',
    reqTitle: 'Lo que exigen todas, y cómo se cumple',
    reqHead: ['Requisito', 'Qué exige la norma', 'En BlueCMR'],
    req: [
      { title: 'Integridad', text: 'El documento no puede alterarse sin que quede constancia verificable.', aside: 'Huellas criptográficas y PDF firmado inmutable.' },
      { title: 'Identificación del firmante', text: 'Un vínculo claro entre la persona que firma y el acto de firma.', aside: 'Firma avanzada con evidencias de identidad y OTP opcional.' },
      { title: 'Consentimiento', text: 'Una voluntad expresa y atribuible.', aside: 'Aceptación expresa registrada en cada firma.' },
      { title: 'Trazabilidad', text: 'Que las acciones sobre el documento se puedan reconstruir en el tiempo.', aside: 'Registro de eventos del ciclo de vida con sello de tiempo.' },
      { title: 'Accesibilidad', text: 'Que el documento se pueda presentar y verificar cuando se requiera.', aside: 'Acceso para las partes y código QR para las autoridades.' },
    ],
    scopeEyebrow: 'Alcance',
    scopeTitle: 'BlueCMR facilita cumplir; no sustituye las obligaciones de las partes',
    scope: [
      { title: 'Lo que cubre BlueCMR', items: ['La infraestructura para crear, firmar, seguir y conservar los documentos', 'Las evidencias electrónicas de cada documento', 'La adaptación a los cambios de la norma'] },
      { title: 'Lo que corresponde a las partes', items: ['La veracidad de los datos que introducen', 'Las obligaciones contractuales y operativas del transporte', 'El uso del documento conforme a la normativa aplicable'] },
    ],
    closing: '¿Dudas sobre la norma que le aplica?',
    closingText: 'Revisamos su caso y le enseñamos cómo lo resuelve BlueCMR, sin compromiso.',
    breadcrumb: ['Inicio', 'Confianza', 'Cumplimiento normativo'],
  },
  en: {
    seoTitle: 'Regulatory compliance of digital transport documents | BlueCMR',
    seoDescription: 'The rules BlueCMR transport documents comply with: eIDAS, the eCMR Protocol, eFTI, ADR, Spanish transport law and the DeCA control document, ENS and GDPR.',
    seoKeywords: 'transport regulatory compliance, eidas, ecmr protocol, efti, adr, ens, gdpr',
    heroEyebrow: 'Trust · Regulatory compliance',
    heroTitle: 'The rules every document complies with',
    heroText: 'BlueCMR is designed for the technical and legal requirements of European and Spanish rules on road transport documents, electronic signatures and information security.',
    heroChecks: ['eIDAS', 'eCMR Protocol', 'eFTI', 'ENS'],
    head: ['Rule', 'What it governs', 'How BlueCMR applies it'],
    groups: [
      {
        title: 'Europe and international',
        rows: [
          { title: 'eIDAS Regulation', sub: '(EU) 910/2014', href: EURLEX('en', '32014R0910'), text: 'Electronic identification and trust services: electronic signatures, seals and timestamps.', aside: 'Advanced electronic signature (Art. 26) and timestamps on every document.' },
          { title: 'eCMR Protocol', sub: 'CMR Convention, Geneva, 2008', text: 'The electronic consignment note in international road transport.', aside: 'eCMR with the same validity as paper in the countries that have ratified it.' },
          { title: 'eFTI Regulation', sub: '(EU) 2020/1056', href: EURLEX('en', '32020R1056'), text: 'Electronic exchange of transport information with authorities, who must accept it from July 2027 through certified platforms.', aside: 'Each document\'s data follows the eFTI common data set.' },
          { title: 'ADR', sub: 'Chapter 5.4', text: 'Documentation for the carriage of dangerous goods by road.', aside: 'Transport document with the particulars of chapter 5.4, together with the control document.' },
        ],
      },
      {
        title: 'Spain',
        rows: [
          { title: 'Law 15/2009', sub: 'Land transport contract', text: 'The transport contract and the consignment note.', aside: 'Digital consignment note with the data it requires.' },
          { title: 'Order FOM/2861/2012', href: 'https://www.boe.es/buscar/doc.php?id=BOE-A-2013-154', text: 'The control document in road transport.', aside: 'Control document (DeCA) with all its data, also within the consignment note or delivery note.' },
          { title: 'Law 9/2025', sub: 'Sustainable Mobility', href: 'https://www.boe.es/buscar/act.php?id=BOE-A-2025-24545', text: 'Mandatory electronic format for the control document.', aside: 'Control document in electronic form only.' },
          { title: 'Resolution of 5 June 2026', href: 'https://www.boe.es/buscar/doc.php?id=BOE-A-2026-12784', text: 'Technical requirements of the electronic control document.', aside: 'PDF with QR code and the technical requirements of the Resolution.' },
        ],
      },
      {
        title: 'Security and data',
        rows: [
          { title: 'National Security Framework (ENS)', sub: 'Royal Decree 311/2022', to: url('/confianza/seguridad'), text: 'Security of information systems.', aside: 'BLUECMR, S.L.U. holds a management system certified under Royal Decree 311/2022 (ENS) by OCA CERT.' },
          { title: 'GDPR', sub: '(EU) 2016/679', href: EURLEX('en', '32016R0679'), text: 'Protection of personal data.', aside: 'Data hosted in the European Union and a data processing agreement (DPA).' },
        ],
      },
    ],
    reqEyebrow: 'Common requirements',
    reqTitle: 'What they all require, and how it is met',
    reqHead: ['Requirement', 'What the rules require', 'In BlueCMR'],
    req: [
      { title: 'Integrity', text: 'The document cannot be altered without a verifiable record.', aside: 'Cryptographic hashes and an immutable signed PDF.' },
      { title: 'Signer identification', text: 'A clear link between the person signing and the act of signing.', aside: 'Advanced signature with identity evidence and optional OTP.' },
      { title: 'Consent', text: 'An express intent that can be attributed.', aside: 'Express acceptance recorded with every signature.' },
      { title: 'Traceability', text: 'Actions on the document can be reconstructed over time.', aside: 'Lifecycle event log with timestamps.' },
      { title: 'Accessibility', text: 'The document can be presented and verified when required.', aside: 'Access for the parties and a QR code for the authorities.' },
    ],
    scopeEyebrow: 'Scope',
    scopeTitle: 'BlueCMR makes compliance easier; it does not replace the parties\' obligations',
    scope: [
      { title: 'What BlueCMR covers', items: ['The infrastructure to create, sign, track and preserve documents', 'The electronic evidence of every document', 'Adapting to regulatory changes'] },
      { title: 'What is up to the parties', items: ['The accuracy of the data they enter', 'The contractual and operational obligations of the transport', 'Using the document in line with the applicable rules'] },
    ],
    closing: 'Questions about the rules that apply to you?',
    closingText: 'We review your case and show you how BlueCMR handles it, with no commitment.',
    breadcrumb: ['Home', 'Trust', 'Regulatory compliance'],
  },
}[lang]);

const CumplimientoNormativoPage = () => {
  const { i18n } = useTranslation();
  const lang = i18n.language === 'es' ? 'es' : 'en';
  const { getLocalizedUrl } = useLocalizedUrl();
  const c = copy(lang, getLocalizedUrl);
  const primaryCta = usePrimaryCta(lang);

  return (
    <>
      <SeoMeta
        title={c.seoTitle}
        description={c.seoDescription}
        canonical={PAGE_URL[lang]}
        type="website"
        alternates={{ es: PAGE_URL.es, en: PAGE_URL.en, 'x-default': PAGE_URL.es }}
        keywords={c.seoKeywords}
        breadcrumbs={[
          { name: c.breadcrumb[0], url: lang === 'es' ? 'https://www.bluecmr.com/' : 'https://www.bluecmr.com/en' },
          { name: c.breadcrumb[2], url: PAGE_URL[lang] },
        ]}
      />

      <div className="bg-white">
        <PageHero eyebrow={c.heroEyebrow} title={c.heroTitle} text={c.heroText} image={HERO_IMAGE} primary={primaryCta} compact>
          <HeroChecks items={c.heroChecks} />
        </PageHero>

        {/* Marco normativo, por bloques */}
        <Section tone="white">
          {c.groups.map((g, i) => (
            <div key={g.title} className={i > 0 ? 'mt-16' : ''}>
              <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-slate-900 mb-8">{g.title}</h2>
              <RowsTable head={c.head} rows={g.rows} asideTone="plain" />
            </div>
          ))}
        </Section>

        <Section tone="gray">
          <SectionIntro eyebrow={c.reqEyebrow} title={c.reqTitle} />
          <RowsTable head={c.reqHead} rows={c.req} asideTone="plain" />
        </Section>

        <Section tone="white">
          <SectionIntro eyebrow={c.scopeEyebrow} title={c.scopeTitle} />
          <TwoColumns cols={c.scope} />
        </Section>

        <RelatedSection title={RELATED_TITLE[lang]} items={otherTrustPages(lang, 'cumplimiento', getLocalizedUrl)} />

        <ClosingCta title={c.closing} text={c.closingText} lang={lang} />
      </div>
    </>
  );
};

export default CumplimientoNormativoPage;
